import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ExpandMoreRounded,
    RefreshRounded,
    QueueRounded,
    CircleRounded,
    KeyboardArrowDownRounded,
    KeyboardArrowUpRounded,
} from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import server from '../../../services/server';
import useErrors from '../../../hooks/useErrors';
import { useBrokerQueueSocket } from '../../../hooks/useBrokerQueueSocket';

function StateChip({ state }) {
    const color =
        state === 'active'
            ? 'primary'
            : state === 'delayed'
              ? 'warning'
              : state === 'waiting'
                ? 'info'
                : state === 'failed'
                  ? 'error'
                  : 'default';
    return <Chip size="small" label={state || '—'} color={color} variant="outlined" />;
}

function MetricCard({ label, value, highlight }) {
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" display="block">
                    {label}
                </Typography>
                <Typography variant="h4" fontWeight={700} color={highlight ? 'primary.main' : 'text.primary'}>
                    {value ?? '—'}
                </Typography>
            </CardContent>
        </Card>
    );
}

function jobTypeLabel(row, labelMap) {
    const type = row?.data?.type || row?.name;
    if (labelMap?.[type]) return labelMap[type];
    if (labelMap?.[row?.name]) return labelMap[row.name];
    return row?.name || type || '—';
}

function JobPayloadSummary({ data }) {
    if (!data || typeof data !== 'object') return '—';
    const bits = [];
    if (data.type) bits.push(data.type);
    if (data.awinAdvertiserId) bits.push(`adv ${data.awinAdvertiserId}`);
    if (data.brandId) bits.push(`brand ${data.brandId}`);
    if (data.campaignId) bits.push(`campaign ${data.campaignId}`);
    if (data.promotion?.promotionId) bits.push(`promo ${data.promotion.promotionId}`);
    if (data.product?.id) bits.push(`product ${data.product.id}`);
    if (data.advertiserId) bits.push(`internal ${data.advertiserId}`);
    return bits.length ? bits.join(' · ') : 'payload';
}

function JobRow({ row, labelMap }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <TableRow hover>
                <TableCell padding="checkbox">
                    <IconButton size="small" onClick={() => setOpen(v => !v)} aria-label="detalhes">
                        {open ? <KeyboardArrowUpRounded fontSize="small" /> : <KeyboardArrowDownRounded fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {row.opts?.jobId || row.id}
                </TableCell>
                <TableCell>{jobTypeLabel(row, labelMap)}</TableCell>
                <TableCell>
                    <StateChip state={row.state} />
                </TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap color="text.secondary">
                        <JobPayloadSummary data={row.data} />
                    </Typography>
                </TableCell>
                <TableCell align="right">{row.attemptsMade ?? 0}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box py={1.5} px={1}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                Payload
                            </Typography>
                            <Box
                                component="pre"
                                sx={{
                                    m: 0,
                                    p: 1.5,
                                    bgcolor: 'action.hover',
                                    borderRadius: 1,
                                    fontSize: 11,
                                    overflow: 'auto',
                                    maxHeight: 220,
                                }}
                            >
                                {JSON.stringify(row.data, null, 2)}
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

/**
 * @param {{
 *   broker: 'awin'|'lomadee',
 *   title: string,
 *   subtitle?: string,
 *   fetchUrl: string,
 *   jobLabelMap?: Record<string, string>,
 *   helpItems?: string[],
 *   actions: Array<{
 *     key: string,
 *     step?: string,
 *     title: string,
 *     description: string,
 *     buttonLabel: string,
 *     variant?: 'contained'|'outlined',
 *     color?: string,
 *     startIcon?: React.ReactNode,
 *     request: () => Promise<any>,
 *     successMessage?: (data: any, body: any) => string,
 *   }>,
 * }} props
 */
export default function BrokerQueuePage({
    broker,
    title,
    subtitle,
    fetchUrl,
    jobLabelMap,
    helpItems = [],
    actions = [],
}) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const [loading, setLoading] = useState(null);

    const { data, error, mutate, isLoading, isValidating } = useSWR(
        fetchUrl ? { url: fetchUrl } : null,
        async ({ url }) => {
            const response = await server.get(url);
            return response?.data;
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            refreshInterval: 0,
            errorRetryCount: 2,
        },
    );

    const payload = data?.data;
    const counts = payload?.counts;
    const jobs = payload?.jobs ?? [];

    const onSocketUpdate = useCallback(() => {
        mutate();
    }, [mutate]);

    const { connected } = useBrokerQueueSocket(broker, { onUpdate: onSocketUpdate });

    useEffect(() => {
        if (error) setErrors(error);
    }, [error, setErrors]);

    const metricKeys = useMemo(
        () => [
            { key: 'waiting', label: 'Waiting', highlight: true },
            { key: 'active', label: 'Active', highlight: true },
            { key: 'delayed', label: 'Delayed' },
            { key: 'completed', label: 'Completed' },
            { key: 'failed', label: 'Failed' },
        ],
        [],
    );

    const run = async (action) => {
        try {
            setLoading(action.key);
            const axiosRes = await action.request();
            const body = axiosRes?.data;
            const d = body?.data;
            const text =
                (typeof action.successMessage === 'function' && action.successMessage(d, body)) ||
                body?.message ||
                'Concluído.';
            enqueueSnackbar(text, { variant: 'success' });
            await mutate();
        } catch (e) {
            setErrors(e);
        } finally {
            setLoading(null);
        }
    };

    const refreshing = isValidating || isLoading;

    return (
        <Container maxWidth="xl">
            <Stack spacing={3}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
                    <Stack spacing={0.5}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <QueueRounded color="primary" />
                            <Typography variant="h2">{title}</Typography>
                        </Stack>
                        {subtitle ? (
                            <Typography variant="body2" color="text.secondary" maxWidth={720}>
                                {subtitle}
                            </Typography>
                        ) : null}
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Tooltip title={connected ? `Sala broker:${broker}` : 'Socket desconectado — use Atualizar'}>
                            <Chip
                                size="small"
                                icon={<CircleRounded sx={{ fontSize: '10px !important' }} />}
                                label={connected ? 'Ao vivo' : 'Offline'}
                                color={connected ? 'success' : 'default'}
                                variant={connected ? 'filled' : 'outlined'}
                            />
                        </Tooltip>
                        <Button
                            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshRounded />}
                            variant="outlined"
                            onClick={() => mutate()}
                            disabled={refreshing}
                        >
                            Atualizar
                        </Button>
                    </Stack>
                </Stack>

                <Stack direction="row" flexWrap="wrap" gap={1.5} width="100%">
                    {metricKeys.map(m => (
                        <Box key={m.key} sx={{ flex: '1 1 0', minWidth: { xs: 'calc(50% - 12px)', sm: 0 } }}>
                            <MetricCard label={m.label} value={counts?.[m.key]} highlight={m.highlight} />
                        </Box>
                    ))}
                </Stack>

                <Box>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        Ações
                    </Typography>
                    <Grid container spacing={2}>
                        {actions.map((action, index) => {
                            const mdCols =
                                actions.length === 1 ? 12 : actions.length === 3 ? 4 : actions.length >= 4 ? 3 : 6;
                            return (
                            <Grid item xs={12} sm={actions.length === 3 ? 4 : 6} md={mdCols} key={action.key}>
                                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flex: 1 }}>
                                        <Stack direction="row" alignItems="center" gap={1} mb={1}>
                                            {action.step ? (
                                                <Chip size="small" label={action.step} color="primary" variant="outlined" />
                                            ) : (
                                                <Chip size="small" label={String(index + 1)} variant="outlined" />
                                            )}
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                {action.title}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {action.description}
                                        </Typography>
                                    </CardContent>
                                    <CardActions sx={{ px: 2, pb: 2 }}>
                                        <Button
                                            variant={action.variant || 'contained'}
                                            color={action.color || 'primary'}
                                            startIcon={
                                                loading === action.key ? (
                                                    <CircularProgress size={16} color="inherit" />
                                                ) : (
                                                    action.startIcon
                                                )
                                            }
                                            disabled={Boolean(loading)}
                                            onClick={() => run(action)}
                                        >
                                            {action.buttonLabel}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                            );
                        })}
                    </Grid>
                </Box>

                {helpItems.length > 0 ? (
                    <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                            <Typography variant="body2" fontWeight={600}>
                                Como funciona
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={1}>
                                {helpItems.map((item, i) => (
                                    <Alert key={i} severity="info" variant="outlined" sx={{ py: 0.5 }}>
                                        {item}
                                    </Alert>
                                ))}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                ) : null}

                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                            <Typography variant="subtitle1" fontWeight={700}>
                                Jobs pendentes
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                waiting · delayed · active · paused
                            </Typography>
                        </Stack>

                        {isLoading && !payload ? (
                            <Box py={4} display="flex" justifyContent="center">
                                <CircularProgress size={32} />
                            </Box>
                        ) : jobs.length === 0 ? (
                            <Typography color="text.secondary">
                                Nenhum job pendente nestes estados — normal se o worker já processou tudo.
                            </Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>Job ID</TableCell>
                                        <TableCell>Tipo</TableCell>
                                        <TableCell>Estado</TableCell>
                                        <TableCell>Resumo</TableCell>
                                        <TableCell align="right">Tentativas</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {jobs.map(row => (
                                        <JobRow key={row.id} row={row} labelMap={jobLabelMap} />
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </Stack>
        </Container>
    );
}
